import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { 
  Download, 
  Eye, 
  Award, 
  Briefcase, 
  GraduationCap, 
  Code, 
  Database,
  BarChart3,
  Mail,
  Phone,
  MapPin,
  Globe,
  ExternalLink
} from "lucide-react";

const Resume = () => {
  const personalInfo = {
    name: "Jatin Mukati",
    title: "Data Analyst",
    email: "jatinmukati44@email.com",
    phone: "+91 6264136612",
    location: "Indore, Madhya Pradesh, India",
    website: "https://jatinmukati.vercel.app",
    linkedin: "https://linkedin.com/in/jatinmukati",
    github: "https://github.com/jatinmukati"
  };

  const summary = "Passionate Data Analyst with Google Data Analytics certification and hands-on experience in SQL, Python, Tableau, and Excel. Proven track record of transforming complex datasets into actionable insights that drive business decisions. Seeking to leverage analytical skills and business acumen to contribute to data-driven organizations.";

  const experience = [
    {
      title: "Data Visualization Intern",
      company: "Infosys Springboard",
      duration: "Aug 2025 - Present",
      location: "Remote",
      responsibilities: [
        "Collaborated in a team project on the World Income Inequality Database (WIID) (~25,000+ records, 180+ countries), where each member proposed and tested their own data cleaning approaches",
        "Independently applied advanced pre-processing techniques (Median, Mode, Interpolation, KNN) to handle 20%+ missing values and resolve outliers, improving dataset quality for analysis",
        "Currently developing Power BI dashboards and preparing Streamlit integration to enable interactive visualization of global/regional inequality metrics (Gini, Palma, quintiles, deciles)",
      ]
    }
  ];

  const education = [
    {
      degree: "Master of Business Administration (MBA) - Finance And Marketing",
      institution: "Shri Govindram Seksaria Institute of Technology and Science, Indore",
      duration: "2023 - 2025",
      cgpa: "7.43",
      relevant: ["Business Analytics", "Marketing Analytics", "Data-Driven Decision Making"]
    },
    {
      degree: "Bachelor of Commerce (B.Com) - Computer Application",
      institution: "Maharaja Ranjit Singh College of Professional Sciences, Indore",
      duration: "2020 - 2023",
      cgpa: "7.68",
      relevant: ["Accounting", "Economics", "Business Statistics"]
    }
  ];

  const certifications = [
    {
      name: "Google Data Analytics Professional Certificate",
      issuer: "Coursera",
      date: "2024",
      link: "https://www.credly.com/badges/6e6f4129-d56f-45ee-a721-345596ec48bf/linked_in_profile"
    },
    {
      name: "Career Essentials in Data Analysis by Microsoft & LinkedIn",
      issuer: "LinkedIn",
      date: "2024",
      link: "https://www.linkedin.com/learning/certificates/d3e89c44fe7e57e1671f4545b896a470b32fce0d9e3d03d5caff1c413c336823"
    },
    {
      name: "Database Management System - Science Graduates",
      issuer: "Infosys Springboard",
      date: "2024",
      links: "GHI345678"
    },
    {
      name: "Accenture North America's Data Analytics and Visualization",
      issuer: "Forage",
      date: "2024",
      link: "https://forage-uploads-prod.s3.amazonaws.com/completion-certificates/Accenture%20North%20America/hzmoNKtzvAzXsEqx8_Accenture%20North%20America_p884fx38b97TGcsJx_1720241904371_completion_certificate.pdf"
    }
  ];

  const skills = {
    "Programming Languages": ["Python", "R", "SQL"],
    "Data Visualization": ["Tableau", "Power BI", "Matplotlib", "Seaborn"],
    "Databases": ["MySQL", "PostgreSQL", "MongoDB"],
    "Tools & Software": ["Excel", "Google Analytics", "Jupyter", "Git"],
    "Statistical Analysis": ["Hypothesis Testing", "Regression Analysis", "A/B Testing"],
    "Soft Skills": ["Problem Solving", "Communication", "Team Collaboration", "Critical Thinking"]
  };

  const projects = [
    {
      name: "Cyclistic Bike-Share Analysis",
      description: "Analyzed bike-sharing patterns to increase annual memberships",
      impact: "Identified strategies to increase memberships by 25%"
    },
    {
      name: "Global Layoffs Analysis",
      description: "Comprehensive analysis of layoff trends across industries",
      impact: "Built predictive models with 85% accuracy"
    },
    {
      name: "Vendor Performance Analysis",
      description: "Analyzed vendor efficiency, inventory turnover, and purchasing strategies to optimize profitability",
      impact: "Identified $2.71M unsold inventory and reduced unit costs by 72% through bulk purchasing insights"
    },
    /*{
      name: "Agriculture Data Analytics",
      description: "Crop yield prediction and optimization analysis",
      impact: "Helped increase farm productivity by 15%"
    }*/
  ];

  const handleDownload = () => {
    // Direct download using static PDF file
    const link = document.createElement('a');
    link.href = '/Jatin_Mukati_Resume.pdf';
    link.download = 'Jatin_Mukati_Resume.pdf';
    link.click();
  };

  const handleViewPDF = () => {
    // Direct view using static PDF file
    window.open('/Jatin_Mukati_Resume.pdf', '_blank');
  };

  return (
    <div className="min-h-screen py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8" data-aos="fade-up">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Resume
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            Download or view my complete professional resume
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={handleDownload} size="lg" className="bg-blue-600 hover:bg-blue-800">
              <Download className="mr-2 h-4 w-4" />
              Download PDF
            </Button>
            <Button onClick={handleViewPDF} variant="outline" size="lg">
              <Eye className="mr-2 h-4 w-4" />
              View PDF
            </Button>
          </div>
        </div>

        {/* Resume Content */}
        <div className="space-y-8">
          {/* Personal Information */}
          <Card className="glassmorphism" data-aos="fade-up" data-aos-delay="100">
            <CardHeader>
              <CardTitle className="text-2xl text-center">{personalInfo.name}</CardTitle>
              <p className="text-lg text-center text-gray-600 dark:text-gray-400">{personalInfo.title}</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-gray-500" />
                  <span>{personalInfo.email}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-gray-500" />
                  <span>{personalInfo.phone}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-gray-500" />
                  <span>{personalInfo.location}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="h-4 w-4 text-gray-500" />
                  <span>{personalInfo.website}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Professional Summary */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Professional Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                {summary}
              </p>
            </CardContent>
          </Card>

          {/* Experience */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="300">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Briefcase className="h-5 w-5" />
                Professional Experience
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {experience.map((job, index) => (
                  <div key={index} className={index !== experience.length - 1 ? "pb-6 border-b border-gray-200 dark:border-gray-700" : ""}>
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {job.title}
                        </h3>
                        <p className="text-blue-600 dark:text-blue-400 font-medium">
                          {job.company}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {job.duration}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {job.location}
                        </p>
                      </div>
                    </div>
                    <ul className="space-y-1 text-gray-600 dark:text-gray-400">
                      {job.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <div className="w-1.5 h-1.5 bg-blue-600 dark:bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                          <span>{resp}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Education */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="400">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Education
              </CardTitle>
            </CardHeader>
            <CardContent>
              {education.map((edu, index) => (
                <div key={index}>
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-2">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {edu.degree}
                      </h3>
                      <p className="text-blue-600 dark:text-blue-400 font-medium">
                        {edu.institution}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {edu.duration}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        CGPA: {edu.cgpa}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 dark:text-gray-400 mb-2">
                    <strong>Relevant Coursework:</strong> {edu.relevant.join(", ")}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Certifications */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="500">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="h-5 w-5" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {cert.name}
                      </h3>
                      <p className="text-blue-600 dark:text-blue-400">
                        {cert.issuer}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        <a
                          href={cert.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="hover:text-blue-600 dark:hover:text-blue-400"
                        >
                        Credentials <ExternalLink className="h-5 w-5" />
                        </a>
                      </p>
                    </div>
                    <Badge variant="secondary">
                      {cert.date}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Skills */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="600">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                Technical Skills
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(skills).map(([category, skillList]) => (
                  <div key={category}>
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                      {category}
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {skillList.map((skill) => (
                        <Badge key={skill} variant="outline">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Key Projects */}
          <Card className="glassmorphism-card" data-aos="fade-up" data-aos-delay="700">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-5 w-5" />
                Key Projects
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {projects.map((project, index) => (
                  <div key={index} className={index !== projects.length - 1 ? "pb-4 border-b border-gray-200 dark:border-gray-700" : ""}>
                    <h4 className="font-semibold text-gray-900 dark:text-white">
                      {project.name}
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400 mb-1">
                      {project.description}
                    </p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">
                      <strong>Impact:</strong> {project.impact}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Download Section */}
        <div className="mt-12 text-center">
          <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 border" data-aos="fade-up" data-aos-delay="800">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Need a PDF copy?
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Download my resume in PDF format for your records
              </p>
              <Button onClick={handleDownload} size="lg" className="bg-blue-600 hover:bg-blue-700">
                <Download className="mr-2 h-4 w-4" />
                Download Resume PDF
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Resume;